import { useEffect, useRef, type ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'

const AUTH_TIMEOUT_MS = 3_000

export function AuthProvider({ children }: { children: ReactNode }) {
  const { setSession, refreshProfile, setLoading, reset } = useAuthStore()
  const didInit = useRef(false)

  useEffect(() => {
    // Safety timeout: if INITIAL_SESSION never fires, unblock the app
    const timeout = setTimeout(() => {
      if (!didInit.current) {
        didInit.current = true
        setLoading(false)
      }
    }, AUTH_TIMEOUT_MS)

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session)

      if (event === 'INITIAL_SESSION') {
        // Unblock the app immediately — don't wait for profile fetch
        didInit.current = true
        setLoading(false)
      }

      if (
        (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') &&
        session?.user
      ) {
        // Fire profile fetch in background — doesn't block app render
        void refreshProfile()
      }

      if (event === 'SIGNED_OUT') {
        reset()
      }
    })

    return () => {
      clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [setSession, refreshProfile, setLoading, reset])

  return <>{children}</>
}
